import { AxiosError } from "axios";
import {
    UseMutationOptions,
    UseMutationResult,
    useMutation,
    useQueryClient,
} from "react-query";

import { useAuth } from "../../sdk/hooks";
import { IOrder } from "../../sdk/types";
import { api } from "../../sdk/utils";

export interface IReimportCustomizedOrdersParameters {
    file: File;
}

export const useReimportCustomizedOrders = (
    invalidQueryKeys?: string | string[],
    options?: UseMutationOptions<
        IOrder[],
        AxiosError,
        IReimportCustomizedOrdersParameters,
        unknown
    >,
): UseMutationResult<
    IOrder[],
    AxiosError,
    IReimportCustomizedOrdersParameters
> => {
    const auth = useAuth();
    const queryClient = useQueryClient();

    return useMutation<
        IOrder[],
        AxiosError,
        IReimportCustomizedOrdersParameters
    >({
        ...options,
        mutationFn: (
            parameters: IReimportCustomizedOrdersParameters,
        ): Promise<IOrder[]> => {
            if (!auth.token) {
                throw new Error(
                    "Cannot import orders without authorization token.",
                );
            }

            return api.orders.Order.reimportCustomizedOrders({
                token: auth.token,
                file: parameters.file,
            });
        },
        onError: options?.onError,
        onSuccess: (
            orders: IOrder[],
            variables: IReimportCustomizedOrdersParameters,
            context: unknown,
        ) => {
            if (invalidQueryKeys !== undefined) {
                queryClient.invalidateQueries(invalidQueryKeys);
            }

            options?.onSuccess?.(orders, variables, context);
        },
    });
};
